import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  FormControl,
  Select,
  MenuItem,
  Badge,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  TableSortLabel,
  OutlinedInput,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Assessment,
  Download,
  FilterList,
  Star,
  EmojiEvents,
  Verified,
  Warning,
  Person,
  Business,
  AccountBalance,
  Groups,
  ManageAccounts,
  Flag,
  Visibility,
  NotificationImportant,
  CheckCircle,
  Schedule,
  SwapHoriz,
  VolumeUp,
  Analytics,
  Refresh,
  CalendarToday,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';

interface Host {
  id: string;
  rank: number;
  name: string;
  location: string;
  region: string;
  type: 'Owner' | 'Broker' | 'Fleet' | 'Liaison' | 'Manager';
  verificationPercentage: number;
  disputeRate: number;
  swapRate: number;
  volume: number;
  growthRate: number; // Percentage growth (e.g., 24 = 24%)
  badges: string[];
  status: 'Active' | 'Inactive' | 'Suspended';
  avatar?: string;
}

interface CategoryFlag {
  id: string;
  title: string;
  description: string;
  category: 'Liaison' | 'Broker' | 'Manager';
  flaggedDate: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Pending' | 'Under Review' | 'Resolved';
}

export const HostAnalyticsPage: React.FC = () => {
  const [selectedHostType, setSelectedHostType] = useState('All Types');
  const [dateFilter, setDateFilter] = useState('Last 30 Days');
  const [regionFilter, setRegionFilter] = useState('All Regions');
  const [sortBy, setSortBy] = useState('volume');
  const [performanceMetric, setPerformanceMetric] = useState<'growth' | 'verification' | 'disputes' | 'volume'>('growth');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Mock host performance data
  const allHosts: Host[] = [
    {
      id: '1',
      rank: 1,
      name: 'Lagos Luxury Fleet',
      location: 'Lagos, Nigeria',
      region: 'Lagos',
      type: 'Fleet',
      verificationPercentage: 98,
      disputeRate: 0.8,
      swapRate: 2.1,
      volume: 324,
      growthRate: 24,
      badges: ['verified', 'star', 'trophy'],
      status: 'Active'
    },
    {
      id: '2',
      rank: 2,
      name: 'James O.',
      location: 'Nairobi, Kenya',
      region: 'Nairobi',
      type: 'Owner',
      verificationPercentage: 96,
      disputeRate: 1.2,
      swapRate: 3.4,
      volume: 187,
      growthRate: 18,
      badges: ['verified', 'star'],
      status: 'Active'
    },
    {
      id: '3',
      rank: 3,
      name: 'Accra Premium Cars',
      location: 'Accra, Ghana',
      region: 'Accra',
      type: 'Broker',
      verificationPercentage: 94,
      disputeRate: 2.1,
      swapRate: 5.6,
      volume: 215,
      growthRate: 15,
      badges: ['verified', 'star'],
      status: 'Active'
    },
    {
      id: '4',
      rank: 4,
      name: 'Sarah M.',
      location: 'Lagos, Nigeria',
      region: 'Lagos',
      type: 'Manager',
      verificationPercentage: 92,
      disputeRate: 1.8,
      swapRate: 4.2,
      volume: 176,
      growthRate: 12,
      badges: ['verified'],
      status: 'Active'
    },
    {
      id: '5',
      rank: 5,
      name: 'Lagos Connect',
      location: 'Lagos, Nigeria',
      region: 'Lagos',
      type: 'Liaison',
      verificationPercentage: 87,
      disputeRate: 3.4,
      swapRate: 8.7,
      volume: 154,
      growthRate: 12,
      badges: ['verified'],
      status: 'Active'
    },
    {
      id: '6',
      rank: 6,
      name: 'Abuja Elite Cars',
      location: 'Abuja, Nigeria',
      region: 'Abuja',
      type: 'Fleet',
      verificationPercentage: 91,
      disputeRate: 1.5,
      swapRate: 3.8,
      volume: 198,
      growthRate: 12,
      badges: ['verified', 'star'],
      status: 'Active'
    },
    {
      id: '7',
      rank: 7,
      name: 'Michael K.',
      location: 'Lagos, Nigeria',
      region: 'Lagos',
      type: 'Owner',
      verificationPercentage: 85,
      disputeRate: 2.3,
      swapRate: 6.1,
      volume: 142,
      growthRate: 12,
      badges: ['verified'],
      status: 'Active'
    },
    {
      id: '8',
      rank: 8,
      name: 'Nairobi Premium Fleet',
      location: 'Nairobi, Kenya',
      region: 'Nairobi',
      type: 'Fleet',
      verificationPercentage: 93,
      disputeRate: 1.1,
      swapRate: 2.8,
      volume: 287,
      growthRate: 12,
      badges: ['verified', 'star'],
      status: 'Active'
    },
    {
      id: '9',
      rank: 9,
      name: 'Ghana Brokers Ltd',
      location: 'Accra, Ghana',
      region: 'Accra',
      type: 'Broker',
      verificationPercentage: 88,
      disputeRate: 3.2,
      swapRate: 7.3,
      volume: 165,
      growthRate: 12,
      badges: ['verified'],
      status: 'Active'
    },
    {
      id: '10',
      rank: 10,
      name: 'Abuja Liaison Pro',
      location: 'Abuja, Nigeria',
      region: 'Abuja',
      type: 'Liaison',
      verificationPercentage: 79,
      disputeRate: 4.1,
      swapRate: 9.2,
      volume: 128,
      growthRate: 12,
      badges: ['verified'],
      status: 'Active'
    }
  ];

  // Filter and sort hosts based on selected filters
  const hosts = React.useMemo(() => {
    let filtered = allHosts;

    // Apply region filter
    if (regionFilter !== 'All Regions') {
      filtered = filtered.filter(host => host.region === regionFilter);
    }

    // Apply host type filter
    if (selectedHostType !== 'All Types') {
      filtered = filtered.filter(host => host.type === selectedHostType);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any = a[sortBy as keyof Host];
      let bValue: any = b[sortBy as keyof Host];

      // Handle different data types
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return sorted;
  }, [regionFilter, selectedHostType, sortBy, sortOrder]);

  const categoryFlags: CategoryFlag[] = [
    {
      id: '1',
      title: 'High Swap Rate Alert',
      description: 'Liaison hosts have a 12.5% swap rate, 4% higher than platform average',
      category: 'Liaison',
      flaggedDate: 'June 28, 2025',
      priority: 'High',
      status: 'Pending'
    },
    {
      id: '2',
      title: 'Verification Compliance',
      description: 'Broker verification rate dropped 8% in the past month, below minimum threshold',
      category: 'Broker',
      flaggedDate: 'June 25, 2025',
      priority: 'Medium',
      status: 'Under Review'
    },
    {
      id: '3',
      title: 'Dispute Rate Warning',
      description: 'Liaison dispute rate at 5.4%, exceeding the 4% threshold for platform health',
      category: 'Liaison',
      flaggedDate: 'June 22, 2025',
      priority: 'High',
      status: 'Pending'
    },
    {
      id: '4',
      title: 'SLA Response Time',
      description: 'Manager response time averaging 4.8 hours, approaching the SLA limit',
      category: 'Manager',
      flaggedDate: 'June 20, 2025',
      priority: 'Medium',
      status: 'Pending'
    }
  ];

  // Calculate performance metrics dynamically based on filtered hosts
  const performanceMetrics = React.useMemo(() => {
    if (hosts.length === 0) {
      return {
        verificationPercentage: { value: 0, change: 0, trend: 'up' },
        disputeRate: { value: 0, change: 0, trend: 'down' },
        swapRate: { value: 0, change: 0, trend: 'up' },
        bookingVolume: { value: 0, change: 0, trend: 'up' }
      };
    }

    const avgVerification = Math.round(
      hosts.reduce((sum, host) => sum + host.verificationPercentage, 0) / hosts.length
    );
    const avgDispute = parseFloat(
      (hosts.reduce((sum, host) => sum + host.disputeRate, 0) / hosts.length).toFixed(1)
    );
    const avgSwap = parseFloat(
      (hosts.reduce((sum, host) => sum + host.swapRate, 0) / hosts.length).toFixed(1)
    );
    const totalVolume = hosts.reduce((sum, host) => sum + host.volume, 0);

    return {
      verificationPercentage: { value: avgVerification, change: 4, trend: 'up' },
      disputeRate: { value: avgDispute, change: 0.8, trend: 'down' },
      swapRate: { value: avgSwap, change: 1.2, trend: 'up' },
      bookingVolume: { value: totalVolume, change: 18, trend: 'up' }
    };
  }, [hosts]);

  // Calculate top performers for each metric category
  const topPerformers = React.useMemo(() => {
    // Fastest Growing (by growthRate)
    const fastestGrowing = [...hosts]
      .sort((a, b) => b.growthRate - a.growthRate)
      .slice(0, 5)
      .map(host => ({
        name: host.name,
        value: host.growthRate,
        type: host.type
      }));

    // Highest Verification (by verificationPercentage)
    const highestVerification = [...hosts]
      .sort((a, b) => b.verificationPercentage - a.verificationPercentage)
      .slice(0, 5)
      .map(host => ({
        name: host.name,
        value: host.verificationPercentage,
        type: host.type
      }));

    // Lowest Disputes (by disputeRate - ascending)
    const lowestDisputes = [...hosts]
      .sort((a, b) => a.disputeRate - b.disputeRate)
      .slice(0, 5)
      .map(host => ({
        name: host.name,
        value: host.disputeRate,
        type: host.type
      }));

    // Highest Volume (by volume)
    const highestVolume = [...hosts]
      .sort((a, b) => b.volume - a.volume)
      .slice(0, 5)
      .map(host => ({
        name: host.name,
        value: host.volume,
        type: host.type
      }));

    return {
      growth: fastestGrowing,
      verification: highestVerification,
      disputes: lowestDisputes,
      volume: highestVolume
    };
  }, [hosts]);

  // Calculate host type breakdown dynamically
  const hostTypeBreakdown = React.useMemo(() => {
    const typeGroups = hosts.reduce((acc, host) => {
      if (!acc[host.type]) {
        acc[host.type] = [];
      }
      acc[host.type].push(host);
      return acc;
    }, {} as Record<string, Host[]>);

    const verification = Object.keys(typeGroups).map(type => {
      const hostsOfType = typeGroups[type];
      const avgVerification = Math.round(
        hostsOfType.reduce((sum, h) => sum + h.verificationPercentage, 0) / hostsOfType.length
      );
      return { type, percentage: avgVerification };
    }).slice(0, 3);

    const disputes = Object.keys(typeGroups).map(type => {
      const hostsOfType = typeGroups[type];
      const avgDispute = parseFloat(
        (hostsOfType.reduce((sum, h) => sum + h.disputeRate, 0) / hostsOfType.length).toFixed(1)
      );
      return { type, percentage: avgDispute };
    }).slice(0, 3);

    const swapRate = Object.keys(typeGroups).map(type => {
      const hostsOfType = typeGroups[type];
      const avgSwap = parseFloat(
        (hostsOfType.reduce((sum, h) => sum + h.swapRate, 0) / hostsOfType.length).toFixed(1)
      );
      return { type, percentage: avgSwap };
    }).slice(0, 3);

    const volume = Object.keys(typeGroups).map(type => {
      const hostsOfType = typeGroups[type];
      const totalVol = hostsOfType.reduce((sum, h) => sum + h.volume, 0);
      return { type, count: totalVol };
    }).slice(0, 3);

    return { verification, disputes, swapRate, volume };
  }, [hosts]);

  const getHostTypeIcon = (type: string) => {
    switch (type) {
      case 'Owner': return <Person />;
      case 'Broker': return <Business />;
      case 'Fleet': return <Groups />;
      case 'Liaison': return <AccountBalance />;
      case 'Manager': return <ManageAccounts />;
      default: return <Person />;
    }
  };

  const getHostTypeColor = (type: string) => {
    switch (type) {
      case 'Owner': return 'primary';
      case 'Broker': return 'secondary';
      case 'Fleet': return 'success';
      case 'Liaison': return 'warning';
      case 'Manager': return 'info';
      default: return 'default';
    }
  };

  const getBadgeIcon = (badge: string) => {
    switch (badge) {
      case 'verified': return <Verified sx={{ color: 'primary.main', fontSize: 16 }} />;
      case 'star': return <Star sx={{ color: 'warning.main', fontSize: 16 }} />;
      case 'trophy': return <EmojiEvents sx={{ color: 'success.main', fontSize: 16 }} />;
      default: return null;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'error';
      case 'Medium': return 'warning';
      case 'Low': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'default';
      case 'Suspended': return 'error';
      default: return 'default';
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleApplyDateRange = () => {
    if (customStartDate && customEndDate) {
      setDateFilter('custom');
      setDateRangeDialogOpen(false);
    }
  };

  // Helper function to get display value for the select
  const getDateFilterDisplayValue = () => {
    if (dateFilter === 'custom' && customStartDate && customEndDate) {
      return `${customStartDate} to ${customEndDate}`;
    }
    return dateFilter;
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Host Analytics & Leaderboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Performance metrics and activity levels across host categories
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
            Filter by Days:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={dateFilter}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  setDateRangeDialogOpen(true);
                } else {
                  setDateFilter(e.target.value);
                }
              }}
              renderValue={(value) => getDateFilterDisplayValue()}
              input={
                <OutlinedInput
                  startAdornment={
                    <InputAdornment position="start">
                      <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </InputAdornment>
                  }
                />
              }
            >
              <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
              <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
              <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
              <MenuItem value="custom">Custom Range...</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Download />}
            sx={{ textTransform: 'none' }}
            data-testid="export-report-btn"
            onClick={() => {
              const reportData = [
                'Host Analytics Report',
                `Generated: ${new Date().toLocaleString()}`,
                `Period: ${dateFilter}`,
                `Host Type Filter: ${selectedHostType}`,
                `Region Filter: ${regionFilter}`,
                '',
                'Host Performance Summary:',
                'Total Active Hosts: 127',
                'Average Verification Rate: 88.2%',
                'Average Quality Score: 4.3/5',
                'Total Revenue: ₦45,230,000',
                '',
                'Top Performing Hosts:',
                '1. James Wilson - ₦450,000',
                '2. Sarah Chen - ₦425,000',
                '3. Michael Brown - ₦380,000',
                '',
                'Category Flags Summary:',
                'Pricing Issues: 8 hosts',
                'Communication Delays: 3 hosts',
                'Vehicle Condition: 2 hosts'
              ].join('\n');

              const blob = new Blob([reportData], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `host-analytics-${new Date().toISOString().split('T')[0]}.txt`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Active Filters Indicator */}
      {(selectedHostType !== 'All Types' || regionFilter !== 'All Regions' || dateFilter !== 'Last 30 Days') && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Active Filters:
            </Typography>
            {dateFilter !== 'Last 30 Days' && (
              <Chip
                label={`Period: ${getDateFilterDisplayValue()}`}
                size="small"
                color="primary"
                onDelete={() => setDateFilter('Last 30 Days')}
              />
            )}
            {selectedHostType !== 'All Types' && (
              <Chip
                label={`Type: ${selectedHostType}`}
                size="small"
                color="primary"
                onDelete={() => setSelectedHostType('All Types')}
              />
            )}
            {regionFilter !== 'All Regions' && (
              <Chip
                label={`Region: ${regionFilter}`}
                size="small"
                color="primary"
                onDelete={() => setRegionFilter('All Regions')}
              />
            )}
            <Button
              size="small"
              onClick={() => {
                setSelectedHostType('All Types');
                setRegionFilter('All Regions');
                setDateFilter('Last 30 Days');
              }}
              sx={{ ml: 'auto', textTransform: 'none' }}
            >
              Clear All Filters
            </Button>
          </Box>
        </Alert>
      )}

      {/* Host Type Filter */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
          Filter by Host Type
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {['All Types', 'Owner', 'Broker', 'Fleet', 'Liaison', 'Manager'].map((type) => (
            <Chip
              key={type}
              label={type}
              icon={type !== 'All Types' ? getHostTypeIcon(type) : undefined}
              onClick={() => setSelectedHostType(type)}
              color={selectedHostType === type ? 'primary' : 'default'}
              variant={selectedHostType === type ? 'filled' : 'outlined'}
              sx={{ textTransform: 'none' }}
            />
          ))}
        </Box>
      </Box>

      {/* Performance Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Avg. Verification %
                </Typography>
                <Assessment sx={{ color: 'primary.main' }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {performanceMetrics.verificationPercentage.value}%
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" color="success.main">
                  {performanceMetrics.verificationPercentage.change}%
                </Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">By Host Type:</Typography>
                {hostTypeBreakdown.verification.map((item) => (
                  <Box key={item.type} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption">{item.type}</Typography>
                    <Typography variant="caption">{item.percentage}%</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Dispute Rate
                </Typography>
                <Warning sx={{ color: 'warning.main' }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {performanceMetrics.disputeRate.value}%
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingDown sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" color="success.main">
                  {performanceMetrics.disputeRate.change}%
                </Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">By Host Type:</Typography>
                {hostTypeBreakdown.disputes.map((item) => (
                  <Box key={item.type} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption">{item.type}</Typography>
                    <Typography variant="caption">{item.percentage}%</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Swap Rate
                </Typography>
                <SwapHoriz sx={{ color: 'info.main' }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {performanceMetrics.swapRate.value}%
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ color: 'warning.main', fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" color="warning.main">
                  {performanceMetrics.swapRate.change}%
                </Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">By Host Type:</Typography>
                {hostTypeBreakdown.swapRate.map((item) => (
                  <Box key={item.type} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption">{item.type}</Typography>
                    <Typography variant="caption">{item.percentage}%</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Booking Volume
                </Typography>
                <VolumeUp sx={{ color: 'success.main' }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {performanceMetrics.bookingVolume.value.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" color="success.main">
                  {performanceMetrics.bookingVolume.change}%
                </Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">By Host Type:</Typography>
                {hostTypeBreakdown.volume.map((item) => (
                  <Box key={item.type} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption">{item.type}</Typography>
                    <Typography variant="caption">{item.count}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Host Leaderboard */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Host Leaderboard
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                    Filter by Region:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                      value={regionFilter}
                      onChange={(e) => setRegionFilter(e.target.value)}
                    >
                      <MenuItem value="All Regions">All Regions</MenuItem>
                      <MenuItem value="Lagos">Lagos</MenuItem>
                      <MenuItem value="Abuja">Abuja</MenuItem>
                      <MenuItem value="Nairobi">Nairobi</MenuItem>
                      <MenuItem value="Accra">Accra</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'rank'}
                          direction={sortBy === 'rank' ? sortOrder : 'desc'}
                          onClick={() => handleSort('rank')}
                        >
                          Rank
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'name'}
                          direction={sortBy === 'name' ? sortOrder : 'desc'}
                          onClick={() => handleSort('name')}
                        >
                          Host
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'region'}
                          direction={sortBy === 'region' ? sortOrder : 'desc'}
                          onClick={() => handleSort('region')}
                        >
                          Region
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'type'}
                          direction={sortBy === 'type' ? sortOrder : 'desc'}
                          onClick={() => handleSort('type')}
                        >
                          Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'verificationPercentage'}
                          direction={sortBy === 'verificationPercentage' ? sortOrder : 'desc'}
                          onClick={() => handleSort('verificationPercentage')}
                        >
                          Verification %
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'disputeRate'}
                          direction={sortBy === 'disputeRate' ? sortOrder : 'desc'}
                          onClick={() => handleSort('disputeRate')}
                        >
                          Disputes
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'swapRate'}
                          direction={sortBy === 'swapRate' ? sortOrder : 'desc'}
                          onClick={() => handleSort('swapRate')}
                        >
                          Swap Rate
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'volume'}
                          direction={sortBy === 'volume' ? sortOrder : 'desc'}
                          onClick={() => handleSort('volume')}
                        >
                          Volume
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Badges</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortBy === 'status'}
                          direction={sortBy === 'status' ? sortOrder : 'desc'}
                          onClick={() => handleSort('status')}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hosts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((host) => (
                      <TableRow key={host.id} hover>
                        <TableCell>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {host.rank}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 40, height: 40 }}>
                              {host.name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {host.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {host.location}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {host.region}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={host.type}
                            size="small"
                            color={getHostTypeColor(host.type) as any}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {host.verificationPercentage}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {host.disputeRate}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {host.swapRate}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {host.volume}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {host.badges.map((badge, index) => (
                              <Tooltip key={index} title={badge}>
                                {getBadgeIcon(badge)}
                              </Tooltip>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={host.status}
                            size="small"
                            color={getStatusColor(host.status) as any}
                            variant={host.status === 'Active' ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={hosts.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                {(() => {
                  const start = page * rowsPerPage;
                  const end = Math.min(start + rowsPerPage, hosts.length);
                  return `Showing ${start + 1}-${end} of ${hosts.length} hosts`;
                })()}
                {(selectedHostType !== 'All Types' || regionFilter !== 'All Regions') &&
                  ` (filtered from ${allHosts.length} total)`
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={3}>
            {/* Category Trends */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Host Performance Trends
                    </Typography>
                    <Tooltip title="Select performance metric to visualize">
                      <IconButton size="small">
                        <Analytics />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Metric Selector */}
                  <Box sx={{ mb: 3 }}>
                    <ToggleButtonGroup
                      value={performanceMetric}
                      exclusive
                      onChange={(e, newValue) => newValue && setPerformanceMetric(newValue)}
                      size="small"
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      <ToggleButton value="growth">
                        <TrendingUp sx={{ mr: 0.5, fontSize: 18 }} />
                        Growth
                      </ToggleButton>
                      <ToggleButton value="verification">
                        <CheckCircle sx={{ mr: 0.5, fontSize: 18 }} />
                        Verification
                      </ToggleButton>
                      <ToggleButton value="disputes">
                        <Warning sx={{ mr: 0.5, fontSize: 18 }} />
                        Disputes
                      </ToggleButton>
                      <ToggleButton value="volume">
                        <VolumeUp sx={{ mr: 0.5, fontSize: 18 }} />
                        Volume
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {/* Chart */}
                  <Box sx={{ width: '100%', height: 300, mb: 3 }}>
                    <ResponsiveContainer>
                      <BarChart
                        data={topPerformers[performanceMetric]}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="name" width={90} />
                        <RechartsTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Card sx={{ p: 1.5, minWidth: 200 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {data.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Type: {data.type}
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    {performanceMetric === 'growth' && `Growth: ${data.value}%`}
                                    {performanceMetric === 'verification' && `Verification: ${data.value}%`}
                                    {performanceMetric === 'disputes' && `Dispute Rate: ${data.value}%`}
                                    {performanceMetric === 'volume' && `Volume: ${data.value}`}
                                  </Typography>
                                </Card>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar dataKey="value" fill="#1976d2" radius={[0, 8, 8, 0]}>
                          {topPerformers[performanceMetric].map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={
                                index === 0 ? '#FFD700' :
                                index === 1 ? '#C0C0C0' :
                                index === 2 ? '#CD7F32' :
                                '#1976d2'
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Top 3 Summary Cards */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                    Top 3 Performers
                  </Typography>

                  {topPerformers[performanceMetric].slice(0, 3).map((host, index) => (
                    <Box key={host.name} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32'
                        }}
                      >
                        {index + 1}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {host.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {host.type}
                        </Typography>
                      </Box>
                      <Chip
                        label={
                          performanceMetric === 'growth' ? `${host.value}%` :
                          performanceMetric === 'verification' ? `${host.value}%` :
                          performanceMetric === 'disputes' ? `${host.value}%` :
                          host.value
                        }
                        size="small"
                        color={index === 0 ? 'primary' : 'default'}
                        variant={index === 0 ? 'filled' : 'outlined'}
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* Category Flags */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Category Flags
                    </Typography>
                    <Button variant="text" size="small" sx={{ textTransform: 'none' }}>
                      View All
                    </Button>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    4 active flags across categories
                  </Typography>

                  <List sx={{ p: 0 }}>
                    {categoryFlags.map((flag, index) => (
                      <React.Fragment key={flag.id}>
                        <ListItem sx={{ px: 0, py: 2 }}>
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {flag.title}
                              </Typography>
                              <Chip
                                label={flag.category}
                                size="small"
                                color={getHostTypeColor(flag.category) as any}
                                variant="outlined"
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {flag.description}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                Flagged on {flag.flaggedDate}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'none' }}
                                data-testid="take-action-btn"
                                onClick={() => {
                                  const action = prompt(`Take action for "${flag.title}". Enter action type:\n1. Send Warning\n2. Schedule Review\n3. Temporary Suspension\n4. Escalate\n\nEnter 1-4:`);
                                  if (action && ['1', '2', '3', '4'].includes(action)) {
                                    const actions = ['Send Warning', 'Schedule Review', 'Temporary Suspension', 'Escalate'];
                                    alert(`Action "${actions[parseInt(action) - 1]}" has been initiated for ${flag.title}.`);
                                  }
                                }}
                              >
                                Take Action
                              </Button>
                            </Box>
                          </Box>
                        </ListItem>
                        {index < categoryFlags.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>

                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 2, textTransform: 'none' }}
                    data-testid="manage-flags-btn"
                    onClick={() => {
                      const choice = confirm('Navigate to Flag Management?\n\nOK: View All Flags\nCancel: Flag Management Settings');
                      if (choice) {
                        alert('Redirecting to All Flags view...\n\nNote: This would typically navigate to /users/flags or open a detailed flags management dialog.');
                      } else {
                        alert('Opening Flag Management Settings...\n\nNote: This would typically open settings for flag rules, thresholds, and automated actions.');
                      }
                    }}
                  >
                    Manage Flags
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Date Range Picker Dialog */}
      <Dialog open={dateRangeDialogOpen} onClose={() => setDateRangeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateRangeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleApplyDateRange}
            variant="contained"
            disabled={!customStartDate || !customEndDate}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};